/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadOutscanCpInscanReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";
import urls from "../../../../api/urls";
import { showErrorMessage } from "../../../../utils";

const OutscanVsCpInscanReport = () => {
    const FILTER_STORAGE_KEY = "OUTSCAN_VS_CP_INSCAN_REPORT_FILTERS";
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedDestinationHub, setSelectedDestinationHub] = useState([]);
    const [statusList] = useState([
        { value: "LCR-INSCAN", label: "LCR Inscan" },
        { value: "LCR-OUTSCAN", label: "LCR Outscan" },
        { value: "COMPLETED", label: "Completed" },
        { value: "BOOKED", label: "Booked" },
        { value: "HUB-IN-SCANNED", label: "Hub In-Scanned" },
        { value: "HUB-OUTSCAN", label: "Hub Outscan" },
        // Add more LCR-specific statuses as needed
    ]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [pendingCpSelection, setPendingCpSelection] = useState(null); // Store CP selection to restore after CP list loads

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedDestinationHub,
            selectedStatus,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                // Store CP selection to restore after CP list is loaded
                if (filters.selectedDestinationHub && filters.selectedDestinationHub.length > 0) {
                    setPendingCpSelection(filters.selectedDestinationHub);
                }
                if (filters.selectedStatus && filters.selectedStatus.length > 0) {
                    setSelectedStatus(filters.selectedStatus);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                // Restore filters after hub list is loaded
                restoreFiltersFromStorage();
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        if (!selectedHubs || selectedHubs.length === 0) {
            setCpList([]); // clear CP list if no hub selected
            setSelectedDestinationHub([]); // Clear CP selection when no hubs selected
            setPendingCpSelection(null); // Clear pending CP selection
            return;
        }

        (async () => {
            try {
                const cpLists = await Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)));
                const allCps = [...new Set(cpLists.flat())];
                // console.log("CP List (API Response):", JSON.stringify(allCps, null, 2));
                setCpList(allCps);
            } catch (error) {
                //console.error("Failed to fetch CP list", error);
                message.error("Failed to fetch CP list");
                setCpList([]);
            }
        })();
    }, [selectedHubs]);

    // Restore CP selection after CP list is loaded
    useEffect(() => {
        if (pendingCpSelection && cpList.length > 0) {
            // Filter to only include CPs that exist in the current CP list
            const validCpIds = pendingCpSelection.filter(cpId =>
                cpList.some(cp => cp.value === cpId)
            );
            if (validCpIds.length > 0) {
                setSelectedDestinationHub(validCpIds);
            }
            setPendingCpSelection(null); // Clear pending selection after restoring
        }
    }, [cpList, pendingCpSelection]);
    // Set default hub for HUBADMIN users (only if no hubs are already selected and no filters were restored)
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        let userid = JSON.parse(localStorage.getItem("userId") || "{}");
        setHubUser(user?.roles?.[0]);
        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;

        // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
        const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
        // Only set default hub if no hubs are selected and no filters were saved (to avoid overriding restored filters)
        if (hubList.length > 0 && selectedHubs.length === 0 && !hasSavedFilters) {
            if (activeHubId) {
                const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                }
            } else if ((user?.roles?.[0] === "ROLE_HUBADMIN" || user?.roles?.[0] === "ROLE_HUBOPERATOR")) {
                const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
                if (defaultHub) {
                    setSelectedHubs([defaultHub.value]);
                }
            }
        }
    }, [hubList]);

    const calendarRef = useRef();

    // Custom handler for date range change
    const handleDateRangeChange = (value) => {
        if (!value || !value[0] || !value[1]) {
            // If cleared, reset to today and open calendar
            setDateRange([moment(), moment()]);
            // Open the calendar dropdown
            if (calendarRef.current && calendarRef.current.picker) {
                calendarRef.current.picker.open();
            }
        } else {
            setDateRange(value);
        }
    };

    // Update useEffect to guard against invalid dateRange
    useEffect(() => {
        if (!hubUser) return;
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) return;
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) return;
        // If CPs are selected, wait for CP list to be loaded
        if (selectedDestinationHub && selectedDestinationHub.length > 0 && cpList.length === 0 && selectedHubs.length > 0) {
            return; // Wait for CP list to load
        }
        if (pendingCpSelection) return;
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, selectedHubs, selectedDestinationHub, selectedStatus, dateRange, pagination.current, pagination.pageSize, cpList, pendingCpSelection]);

    const fetchReport = async () => {
        const requestId = createNewRequest();
        setLoading(true);
        let shouldUpdateState = true;
        try {
            const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
            if (!hasValidDates) {
                setLoading(false);
                return;
            }
            if (isLatestRequest(requestId.requestId)) {
                setReportData([]);
            }

            const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
            //console.log("CP List selectedDestinationHub:", JSON.stringify(selectedDestinationHub, null, 2)); 
            const selectedDestHubData = cpList.filter(hub => selectedDestinationHub?.includes(hub.value));

            const params = {
                ...(selectedHubData?.length > 0 && { originHubId: selectedHubData.map(hub => hub.value).join(",") }),
                ...(selectedDestHubData?.length > 0 && { destinationHubId: selectedDestHubData.map(hub => hub.value).join(",") }),
                ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            // console.log("CP List selectedDestinationHubPaeam:", JSON.stringify(params, null, 2)); 

            // Call the Incoming LCR report API

            const { data, total, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getOutScanVsInscanReport(params);

            if (!isLatestRequest(requestId.requestId)) {
                shouldUpdateState = false;
            } else {
                setLastsynced(lastRefreshedAt);
                setNextScheduledRefresh(nextScheduledRefresh);
                setReportData(data);
                setPagination(prev => ({
                    ...prev,
                    total,
                }));
            }
        } catch {
            if (shouldUpdateState && isLatestRequest(requestId.requestId)) {
                showErrorMessage("Failed to fetch report data");
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
            }
        } finally {
            if (shouldUpdateState && isLatestRequest(requestId.requestId)) {
                setLoading(false);
            }
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) {
            showErrorMessage("Please select a date range for download");
            return;
        }
        setDownloadLoading(true);
        try {
            const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
            const selectedDestHubData = cpList.filter(hub => selectedDestinationHub?.includes(hub.value));

            const downloadParams = {
                ...(selectedHubData?.length > 0 && { hubIds: selectedHubData.map(hub => hub.value) }),
                ...(selectedDestHubData?.length > 0 && { cpIds: selectedDestHubData.map(hub => hub.value) }),
                ...(hasValidDates && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") })
            };
            await downloadOutscanCpInscanReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const navigate = useNavigate();

    // Handler for cell clicks (fetch details and navigate)
    const handleCellClick = async (type, record) => {
        // Save current filters before navigating
        saveFiltersToStorage();

        console.log(record, "record");
        const navigationState = {
            fromDate: record.date,
            toDate: record.date,
            date: record.date, // Single date parameter for APIs
            fromHubId: record.fromHubId,
            toCpId: record.cpId,
            hubId: record.fromHubId, // For backward compatibility
            deliveryCpId: record.cpId, // For backward compatibility
            type: type
        };
        if (type === "outscanByHubToCp" || type === "inscanBySameCp" || type === "inscanByOtherCp") {
            navigate("/outscan-by-hub-to-branch", {
                state: navigationState
            });
            return;
        }
        if (type === "drsBySameCp") {
            navigate("/delivery-scan-by-branch", {
                state: navigationState
            });
            return;
        }
        if (type === "drsByOtherCp") {
            navigate("/drs-by-other-cp", {
                state: navigationState
            });
            return;
        }
        if (type === "sameDayDeliveryBySameCp") {
            navigate("/same-day-delivery-report", {
                state: navigationState
            });
            return;
        }
        if (type === "sameDayDeliveryByOtherCp") {
            navigate("/same-day-delivery-by-other-cp", {
                state: navigationState
            });
            return;
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Hub-Outscan Vs CP Inscan Report" navigateTo="/reports">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From & To Date</div>
                        <CalendarComponent
                            ref={calendarRef}
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Origin Hub"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            allowClear
                            mode={hubUser !== "ROLE_HUBADMIN" || hubUser !== "ROLE_HUBOPERATOR" ? "multiple" : "default"}
                            disabled={hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"}
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">CP Name</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select CP"
                            options={cpList} // ✅ Use cpList here
                            onChange={(value) => {
                                setSelectedDestinationHub(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedDestinationHub}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} style={{ textAlign: "right", paddingTop: "22px" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                        >
                            Download Report
                        </Button>
                    </Col>
                    {/* <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Status</div>
                        <DropDownComponent
                            placeholder="Select Status"
                            options={statusList}
                            onChange={(value) => {
                                setSelectedStatus(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedStatus}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>*/}
                </Row>
                <Row style={{ marginTop: 16 }}>

                </Row>
            </Card>
            <TableComponent
                columns={[
                    { title: "Date", dataIndex: "date", key: "date", sorter: true, align: "center", width: 100, },
                    { title: "From Hub", dataIndex: "fromHub", key: "fromHub", sorter: true, align: "center", width: 160, },
                    { title: "To CP", dataIndex: "cpName", key: "cpName", sorter: true, align: "center", width: 160, },
                    {
                        title: "Outscan By Hub To CP",
                        dataIndex: "outscanByHubToCp",
                        key: "outscanByHubToCp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            <span
                                style={{
                                    color: "#1890ff",
                                    cursor: "pointer",
                                    textDecoration: "underline"
                                }}
                                onClick={() => handleCellClick("outscanByHubToCp", record)}
                            >
                                {text}
                            </span>
                        )
                    },
                    {
                        title: "Total Hub Outscan Weight (Kg)",
                        dataIndex: "totalOutscanWeight",
                        key: "totalOutscanWeight",
                        sorter: true,
                        align: "center",
                        width: 160,
                        render: (text, record) => (
                            <span>{text}</span>
                        )
                    },
                    {
                        title: "Inscan By Same CP",
                        dataIndex: "inscanBySameCp",
                        key: "inscanBySameCp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            text && text > 0 ? (
                                <span
                                    style={{
                                        color: "#1890ff",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                    }}
                                    onClick={() => handleCellClick("inscanBySameCp", record)}
                                >
                                    {text}
                                </span>
                            ) : <span>{text || 0}</span>
                        )
                    },
                    {
                        title: "Inscan By Same CP Percentage",
                        dataIndex: "inscanBySameCpPercentage",
                        key: "inscanBySameCpPercentage",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text) => (
                            <span>{text}%</span>
                        )
                    },
                    {
                        title: "Inscan By Other CP",
                        dataIndex: "inscanByOtherCp",
                        key: "inscanByOtherCp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            text && text > 0 ? (
                                <span
                                    style={{
                                        color: "#1890ff",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                    }}
                                    onClick={() => handleCellClick("inscanByOtherCp", record)}
                                >
                                    {text}
                                </span>
                            ) : <span>{text || 0}</span>
                        )
                    },
                    {
                        title: "Inscan By Other CP Percentage",
                        dataIndex: "inscanByOtherCpPercentage",
                        key: "inscanByOtherCpPercentage",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text) => (
                            <span>{text}%</span>
                        )
                    },
                    {
                        title: "DRS By Same CP",
                        dataIndex: "drsBySameCp",
                        key: "drsBySameCp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            text && text > 0 ? (
                                <span
                                    style={{
                                        color: "#1890ff",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                    }}
                                    onClick={() => handleCellClick("drsBySameCp", record)}
                                >
                                    {text}
                                </span>
                            ) : <span>{text || 0}</span>
                        )
                    },
                    {
                        title: "DRS By Same CP Percentage",
                        dataIndex: "drsBySameCpPercentage",
                        key: "drsBySameCpPercentage",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text) => (
                            <span>{text}%</span>
                        )
                    },
                    {
                        title: "DRS By Other CP",
                        dataIndex: "drsByOtherCp",
                        key: "drsByOtherCp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            text && text > 0 ? (
                                <span
                                    style={{
                                        color: "#1890ff",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                    }}
                                    onClick={() => handleCellClick("drsByOtherCp", record)}
                                >
                                    {text}
                                </span>
                            ) : <span>{text || 0}</span>
                        )
                    },
                    {
                        title: "DRS By Other CP Percentage",
                        dataIndex: "drsByOtherCpPercentage",
                        key: "drsByOtherCpPercentage",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text) => (
                            <span>{text}%</span>
                        )
                    },
                    {
                        title: "Same Day Delivery By Same CP",
                        dataIndex: "sameDayDeliveryBySameCp",
                        key: "sameDayDeliveryBySameCp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            text && text > 0 ? (
                                <span
                                    style={{
                                        color: "#1890ff",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                    }}
                                    onClick={() => handleCellClick("sameDayDeliveryBySameCp", record)}
                                >
                                    {text}
                                </span>
                            ) : <span>{text || 0}</span>
                        )
                    },
                    {
                        title: "Same Day Delivery By Same CP Percentage",
                        dataIndex: "sameDayDeliveryBySameCpPercentage",
                        key: "sameDayDeliveryBySameCpPercentage",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text) => (
                            <span>{text}%</span>
                        )
                    },
                    {
                        title: "Same Day Delivery By Other CP",
                        dataIndex: "sameDayDeliveryByOtherCp",
                        key: "sameDayDeliveryByOtherCp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            text && text > 0 ? (
                                <span
                                    style={{
                                        color: "#1890ff",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                    }}
                                    onClick={() => handleCellClick("sameDayDeliveryByOtherCp", record)}
                                >
                                    {text}
                                </span>
                            ) : <span>{text || 0}</span>
                        )
                    },
                    {
                        title: "Same Day Delivery By Other CP Percentage",
                        dataIndex: "sameDayDeliveryByOtherCpPercentage",
                        key: "sameDayDeliveryByOtherCpPercentage",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text) => (
                            <span>{text}%</span>
                        )
                    },
                ]}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey={(record, index) => `${record.date}-${record.fromHubId}-${record.cpId}-${index}`}
                style={{ marginTop: 32 }}
            />
        </div>
    );
};

export default OutscanVsCpInscanReport;
